import { Toaster as Sonner, toast } from "sonner"
import { useEffect, useState } from "react"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ ...props }: ToasterProps) => {
  const [theme, setTheme] = useState<"light" | "dark" | "system">("system")
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    
    // Check for saved dark mode preference
    const savedDarkMode = localStorage.getItem('dark-mode')
    const isDark = savedDarkMode ? JSON.parse(savedDarkMode) : false
    
    // Check system preference if no saved preference
    if (!savedDarkMode) {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      setTheme(systemPrefersDark ? "dark" : "light")
    } else {
      setTheme(isDark ? "dark" : "light")
    }

    // Listen for dark mode changes
    const handleStorageChange = () => {
      const darkMode = localStorage.getItem('dark-mode')
      if (darkMode) {
        setTheme(JSON.parse(darkMode) ? "dark" : "light")
      }
    }

    window.addEventListener('storage', handleStorageChange)
    
    // Watch for class changes on document.documentElement
    const observer = new MutationObserver(() => {
      const isDarkMode = document.documentElement.classList.contains('dark')
      setTheme(isDarkMode ? "dark" : "light")
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      observer.disconnect()
    }
  }, [])

  if (!mounted) return null

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg glass-card",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-theme-gradient group-[.toast]:text-white group-[.toast]:border-none group-[.toast]:hover:scale-105 group-[.toast]:transition-transform",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:hover:bg-muted-hover",
          closeButton:
            "group-[.toast]:bg-background group-[.toast]:text-foreground group-[.toast]:border-border group-[.toast]:hover:bg-muted",
          title: "group-[.toast]:text-foreground group-[.toast]:font-semibold",
          icon: "group-[.toast]:text-theme-primary",
          success: "group-[.toast]:text-green-600 dark:group-[.toast]:text-green-400",
          error: "group-[.toast]:text-red-600 dark:group-[.toast]:text-red-400",
          warning: "group-[.toast]:text-yellow-600 dark:group-[.toast]:text-yellow-400",
          info: "group-[.toast]:text-blue-600 dark:group-[.toast]:text-blue-400",
        },
      }}
      {...props}
    />
  )
}

// Enhanced toast functions with theme integration
const enhancedToast = {
  ...toast,
  
  success: (message: string, options?: Parameters<typeof toast.success>[1]) =>
    toast.success(message, {
      ...options,
      style: {
        background: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
        border: '1px solid hsl(var(--border))',
        ...options?.style,
      },
    }),
    
  error: (message: string, options?: Parameters<typeof toast.error>[1]) =>
    toast.error(message, {
      ...options,
      style: {
        background: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
        border: '1px solid hsl(var(--destructive))',
        ...options?.style,
      },
    }),
    
  warning: (message: string, options?: Parameters<typeof toast>[1]) =>
    toast(message, {
      ...options,
      style: {
        background: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
        border: '1px solid hsl(var(--warning))',
        ...options?.style,
      },
      icon: '⚠️',
    }),
    
  info: (message: string, options?: Parameters<typeof toast>[1]) =>
    toast(message, {
      ...options,
      style: {
        background: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
        border: '1px solid rgb(var(--theme-primary))',
        ...options?.style,
      },
      icon: 'ℹ️',
    }),

  // Theme-aware loading toast
  loading: (message: string, options?: Parameters<typeof toast.loading>[1]) =>
    toast.loading(message, {
      ...options,
      style: {
        background: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
        border: '1px solid rgb(var(--theme-primary))',
        ...options?.style,
      },
    }),

  // Custom themed toast
  themed: (message: string, options?: Parameters<typeof toast>[1]) =>
    toast(message, {
      ...options,
      style: {
        background: 'rgb(var(--theme-primary) / 0.1)',
        color: 'hsl(var(--foreground))',
        border: '1px solid rgb(var(--theme-primary))',
        backdropFilter: 'blur(8px)',
        ...options?.style,
      },
    }),
}

export { Toaster, enhancedToast as toast }
